export type DateRange = { startDate: string; endDate: string };

import { Capability } from "@/global/constants";
import { getTodayYMD } from "@/global";
import { DateTime } from "luxon";
import type { ReportColumn } from "@/features/reporting/interfaces/TemplateInterfaces";

/**
 * Returns true when the given capability only allows a single date filter at a time.
 * Currently applies to MRM (Maersk Risk Management) and Emissions.
 */
export const isSingleDateFilterCapability = (capability: string | undefined): boolean => {
  const cap = capability as Capability;
  return cap === Capability.EMISSIONS || cap === Capability.MAERSK_RISK_MANAGEMENT;
};

/**
 * Returns true when the given capability/reportType combination is the MRM "Disruptions"
 * report type. This report type should never receive a default date (ETA) filter.
 */
export const isMrmDisruptionReportType = (capability: string | undefined, reportType: string | undefined): boolean => {
  return (
    String(capability ?? "").toLowerCase() === String(Capability.MAERSK_RISK_MANAGEMENT).toLowerCase() &&
    String(reportType ?? "")
      .toLowerCase()
      .includes("disrupt")
  );
};

export const SUBSCRIPTION_REPORT_TYPE = "SUBSCRIPTION";

/**
 * Returns true for the Shipment "Subscription" report type. Its Subscribed On filter is a start date
 * (GREATER_THAN, on or after), either a fixed date or a past-relative one (NOW-3M) so it can be scheduled.
 */
export const isSubscriptionReportType = (reportType: string | undefined): boolean =>
  String(reportType ?? "").toUpperCase() === SUBSCRIPTION_REPORT_TYPE;

const normalizeColumnToken = (val: string | undefined | null): string =>
  String(val ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const SUBSCRIBED_ON_ACCEPTED_TOKENS = new Set(["subscribedon", "subscriptionsubscribedon", "subscribedfrom"]);

const TOLERANCE_DAYS_ACCEPTED_TOKENS = new Set(["tolerancedays", "toleranceday", "subscriptiontolerancedays"]);

const STATUS_ACCEPTED_TOKENS = new Set(["status", "subscriptionstatus"]);

export const isToleranceDaysColumn = (
  col: (Pick<ReportColumn, "name" | "label"> & { id?: string }) | undefined | null
): boolean => {
  if (!col) return false;
  return (
    TOLERANCE_DAYS_ACCEPTED_TOKENS.has(normalizeColumnToken((col as any).id)) ||
    TOLERANCE_DAYS_ACCEPTED_TOKENS.has(normalizeColumnToken(col.name)) ||
    TOLERANCE_DAYS_ACCEPTED_TOKENS.has(normalizeColumnToken(col.label))
  );
};

export const isStatusColumn = (
  col: (Pick<ReportColumn, "name" | "label"> & { id?: string }) | undefined | null
): boolean => {
  if (!col) return false;
  return (
    STATUS_ACCEPTED_TOKENS.has(normalizeColumnToken((col as any).id)) ||
    STATUS_ACCEPTED_TOKENS.has(normalizeColumnToken(col.name)) ||
    STATUS_ACCEPTED_TOKENS.has(normalizeColumnToken(col.label))
  );
};

export const isSubscribedOnColumn = (
  col: (Pick<ReportColumn, "name" | "label"> & { id?: string }) | undefined | null
): boolean => {
  if (!col) return false;
  return (
    SUBSCRIBED_ON_ACCEPTED_TOKENS.has(normalizeColumnToken((col as any).id)) ||
    SUBSCRIBED_ON_ACCEPTED_TOKENS.has(normalizeColumnToken(col.name)) ||
    SUBSCRIBED_ON_ACCEPTED_TOKENS.has(normalizeColumnToken(col.label))
  );
};

export const SUBSCRIBED_FROM_DISPLAY_LABEL = "Subscribed From";

export const getSubscriptionColumnDisplayLabel = (
  col: (Pick<ReportColumn, "name" | "label"> & { id?: string }) | undefined | null,
  reportType: string | undefined
): string => {
  if (isSubscriptionReportType(reportType) && col) {
    const matched = SUBSCRIPTION_MANDATORY_COLUMN_CONFIGS.find((cfg) => cfg.matcher(col));
    if (matched?.displayLabel) return matched.displayLabel;
  }
  return String(col?.label ?? col?.name ?? "");
};

export const isSubscriptionMandatoryColumn = (
  col: (Pick<ReportColumn, "name" | "label"> & { id?: string }) | undefined | null,
  reportType?: string
): boolean => {
  if (reportType !== undefined && !isSubscriptionReportType(reportType)) return false;
  return SUBSCRIPTION_MANDATORY_COLUMN_CONFIGS.some((cfg) => cfg.matcher(col));
};

/** Builds field errors without imposing type-specific filter value rules. */
export const getMandatoryFilterErrors = (operator: string | undefined, hasValue: boolean, message: string) => ({
  filterFunction: operator ? "" : message,
  filterValue: hasValue ? "" : message
});

const isHiddenFilterOperator = (operator: string | undefined): boolean =>
  operator === "IS_NULL" || operator === "IS_NOT_NULL";

const toFilterString = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value).trim();
  return "";
};

const hasNonEmptyFilterValue = (value: unknown): boolean => toFilterString(value).length > 0;

const isFutureRelativeDateFunction = (filterFunction: string): boolean => {
  const match = /^NOW([+-])(\d+)/i.exec(String(filterFunction ?? ""));
  return match !== null && match[1] === "+" && Number(match[2]) > 0;
};

export const hasNegativeToleranceDays = (values: unknown[] | undefined | null): boolean => {
  return (values ?? []).some((value) => {
    const trimmed = toFilterString(value);
    if (!trimmed) return false;
    const num = Number(trimmed);
    return !Number.isNaN(num) && num < 0;
  });
};

export const hasFutureSubscribedOnDate = (
  values: unknown[] | undefined | null,
  filterFunctions: string[] | undefined | null,
  today: string = getTodayYMD()
): boolean => {
  const hasFutureValue = (values ?? []).some((value) => {
    const trimmed = toFilterString(value);
    return trimmed.length > 0 && trimmed > today;
  });
  const hasFutureFunction = (filterFunctions ?? []).some(isFutureRelativeDateFunction);
  return hasFutureValue || hasFutureFunction;
};

const hasValidSubscribedOnFilter = (column: ReportColumn, today: string): boolean => {
  const filter = column.filter;
  const hasValues = (filter?.values?.length ?? 0) > 0;
  const hasFunctions = (filter?.filterFunctions?.length ?? 0) > 0;
  const hasFilter = Boolean(filter?.operator && (hasValues || hasFunctions || isHiddenFilterOperator(filter.operator)));
  return hasFilter && !hasFutureSubscribedOnDate(filter?.values, filter?.filterFunctions, today);
};

const hasValidToleranceDaysFilter = (column: ReportColumn): boolean => {
  const filter = column.filter;
  const hasValues = (filter?.values ?? []).some(hasNonEmptyFilterValue);
  return Boolean(
    filter?.operator &&
      (hasValues || isHiddenFilterOperator(filter.operator)) &&
      !hasNegativeToleranceDays(filter?.values)
  );
};

const hasValidStatusFilter = (column: ReportColumn): boolean => {
  const filter = column.filter;
  const hasValues = (filter?.values ?? []).some(hasNonEmptyFilterValue);
  return Boolean(filter?.operator && (hasValues || isHiddenFilterOperator(filter.operator)));
};

export interface SubscriptionMandatoryColumnConfig {
  id: string;
  name: string;
  label: string;
  displayLabel: string;
  dataType: "DATE" | "NUMBER" | "STRING";
  required: boolean;
  defaultFilter: ReportColumn["filter"] | null;
  matcher: (column: (Pick<ReportColumn, "name" | "label"> & { id?: string }) | undefined | null) => boolean;
  hasValidFilter: (column: ReportColumn) => boolean;
}

export const SUBSCRIPTION_MANDATORY_COLUMN_CONFIGS: readonly SubscriptionMandatoryColumnConfig[] = [
  {
    id: "subscribed_on",
    name: "Subscribed On",
    label: "Subscribed On",
    displayLabel: SUBSCRIBED_FROM_DISPLAY_LABEL,
    dataType: "DATE",
    required: true,
    defaultFilter: {
      operator: "GREATER_THAN",
      values: [],
      filterFunctions: ["NOW-3M"]
    },
    matcher: isSubscribedOnColumn,
    hasValidFilter: (column: ReportColumn) => hasValidSubscribedOnFilter(column, getTodayYMD())
  },
  {
    id: "tolerance_days",
    name: "Tolerance Days",
    label: "Tolerance Days",
    displayLabel: "Tolerance Days",
    dataType: "NUMBER",
    required: true,
    defaultFilter: {
      operator: "GREATER_THAN_OR_EQUAL",
      values: ["0"],
      displayableFilter: ["0"],
      filterFunctions: []
    },
    matcher: isToleranceDaysColumn,
    hasValidFilter: hasValidToleranceDaysFilter
  },
  {
    id: "status",
    name: "Status",
    label: "Status",
    displayLabel: "Status",
    dataType: "STRING",
    required: true,
    defaultFilter: null,
    matcher: isStatusColumn,
    hasValidFilter: hasValidStatusFilter
  }
];

export const validateSubscriptionReportColumns = (
  columns: ReportColumn[] = []
): { isValid: boolean; missingColumns: string[]; invalidFilters: string[] } => {
  const missingColumns: string[] = [];
  const invalidFilters: string[] = [];

  for (const config of SUBSCRIPTION_MANDATORY_COLUMN_CONFIGS) {
    const col = columns.find(config.matcher);
    if (!col) {
      missingColumns.push(config.label);
    } else if (!config.hasValidFilter(col)) {
      invalidFilters.push(config.label);
    }
  }

  return {
    isValid: missingColumns.length === 0 && invalidFilters.length === 0,
    missingColumns,
    invalidFilters
  };
};

export enum RequiredMrmDateTerm {
  ETA = "eta",
  ETD = "etd",
  ATA = "ata",
  ATD = "atd"
}

export const isRequiredMrmDateColumn = (col: Pick<ReportColumn, "name" | "label">): boolean => {
  const tokens = new Set(
    `${col.name ?? ""} ${col.label ?? ""}`
      .replaceAll(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
  );

  return Object.values(RequiredMrmDateTerm).some((term) => tokens.has(term));
};

export const buildDefaultFilter = (range: DateRange, fallbackDate: string) => {
  const from = String(range.startDate).trim() || fallbackDate;
  const to = String(range.endDate).trim() || fallbackDate;
  return { operator: "BETWEEN" as const, values: [from, to], filterFunctions: [] as never[] };
};

export default buildDefaultFilter;
import { DefaultRange } from "@/features/reporting/constants/default-range";
import {
  getLastNDaysRange,
  getCurrentMonthRange,
  getRiskManagementRange,
  getPortCongestionRange,
  getLast12MonthsRange,
  getLast3MonthsRange
} from "@/utils/date-ranges";

/**
 * Compute the date range used as the default filter for a capability/reportType.
 * Shared implementation used by the template builder and UI components.
 */
export const computeDateRange = (capDefaults: any, reportType: string): DateRange => {
  if (capDefaults.defaultFrom && capDefaults.defaultTo) {
    return { startDate: String(capDefaults.defaultFrom), endDate: String(capDefaults.defaultTo) };
  }

  switch (capDefaults.defaultRange) {
    case DefaultRange.Last7:
      return getLastNDaysRange(7);
    case DefaultRange.Last3Months:
      return getLast3MonthsRange();
    case DefaultRange.Last30:
      return getLastNDaysRange(30);
    case DefaultRange.CurrentMonth:
      return getCurrentMonthRange();
    case DefaultRange.RiskManagement:
      return getRiskManagementRange();
    case DefaultRange.Emissions:
      return getLast12MonthsRange();
    case DefaultRange.PortCongestion: {
      const isImport = String(reportType ?? "")
        .toLowerCase()
        .includes("import");
      return getPortCongestionRange(isImport);
    }
    default:
      return getLastNDaysRange(30);
  }
};
export { getTodayYMD };
/**
 * Returns a date string YYYY-MM-DD representing a date `days` after the given date (or today).
 * If `fromDate` is invalid, falls back to the calculation from today.
 */
export const getDateAfterDays = (days: number, fromDate?: string): string => {
  let dt = fromDate ? DateTime.fromISO(fromDate) : DateTime.local();
  if (!dt.isValid) {
    const parsed = new Date(fromDate ?? "");
    if (Number.isNaN(parsed.getTime())) {
      dt = DateTime.local();
    } else {
      dt = DateTime.fromJSDate(parsed);
    }
  }
  const result = dt.plus({ days });
  return result.toISODate() ?? getTodayYMD();
};

/**
 * Builds a dynamic date filter (using NOW-relative expressions) for a capability's default range.
 * Used when saving scheduled reports so the filter stays relative to execution time.
 * Returns null for ranges that cannot be expressed as a simple NOW-relative offset (e.g. CurrentMonth).
 */
export const buildDynamicDefaultFilter = (
  capDefaults: { defaultRange?: DefaultRange | string },
  reportType: string
): { operator: "BETWEEN" | "GREATER_THAN"; values: string[]; filterFunctions: string[] } | null => {
  // The Subscription report only takes a start date, so it gets one "on or after" bound, not a range.
  if (isSubscriptionReportType(reportType)) {
    return { operator: "GREATER_THAN", values: [], filterFunctions: ["NOW-3M"] };
  }
  switch (capDefaults.defaultRange) {
    case DefaultRange.RiskManagement:
      return { operator: "BETWEEN", values: [], filterFunctions: ["NOW-60D", "NOW+60D"] };
    case DefaultRange.PortCongestion: {
      const isImport = String(reportType ?? "")
        .toLowerCase()
        .includes("import");
      const offset = isImport ? 10 : 14;
      return { operator: "BETWEEN", values: [], filterFunctions: [`NOW-${offset}D`, `NOW+${offset}D`] };
    }
    case DefaultRange.Last30:
      return { operator: "BETWEEN", values: [], filterFunctions: ["NOW-30D", "NOW+0D"] };
    case DefaultRange.Last7:
      return { operator: "BETWEEN", values: [], filterFunctions: ["NOW-7D", "NOW+0D"] };
    case DefaultRange.Last3Months:
      return { operator: "BETWEEN", values: [], filterFunctions: ["NOW-3M", "NOW+0M"] };
    case DefaultRange.Emissions:
      return { operator: "BETWEEN", values: [], filterFunctions: ["NOW-12M", "NOW+0M"] };
    default:
      return null;
  }
};
import type { ScheduleFrequency, EveryUnit } from "../interfaces/common";
import type { ScheduleFrequencyPayload, TemplateSchedulePayload } from "@/features/reporting/interfaces/schedule";
import { RECURRENCE_MIN, RECURRENCE_MAX } from "../constants/common";

/**
 * Regex for validating a single email address.
 * Covers common formats: local@domain.tld, allows dots and hyphens in local and domain.
 */
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Parses comma-separated email string into trimmed non-empty entries.
 */
export const parseEmailRecipients = (input: string): string[] =>
  input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/**
 * Returns which of the given emails are invalid (do not match EMAIL_REGEX).
 */
export const getInvalidEmails = (emails: string[]): string[] => emails.filter((email) => !EMAIL_REGEX.test(email));

/**
 * Validates comma-separated email recipients.
 * @returns Object with valid flag and list of invalid email addresses (if any).
 */
export const validateEmailRecipients = (input: string): { valid: boolean; invalidEmails: string[] } => {
  const emails = parseEmailRecipients(input);
  const invalidEmails = getInvalidEmails(emails);
  return { valid: invalidEmails.length === 0, invalidEmails };
};

/**
 * Clamps a value to the valid recurrence range (1–6) for Every and day-of-month.
 */
export const clampRecurrenceNumber = (value: number): number =>
  Math.max(RECURRENCE_MIN, Math.min(RECURRENCE_MAX, value));

/**
 * Returns the recurrence unit (Days/Weeks/Months) for a given schedule frequency.
 */
export const getEveryUnitForFrequency = (f: ScheduleFrequency): EveryUnit => {
  if (f === "WEEKLY") return "WEEKS";
  if (f === "MONTHLY") return "MONTHS";
  return "DAYS";
};

/**
 * Derives initial daysOfWeek array from schedule form model value.
 */
export const getInitialDaysOfWeek = (modelValue: { daysOfWeek?: string[] } | undefined): string[] => {
  if (Array.isArray(modelValue?.daysOfWeek) && modelValue.daysOfWeek.length > 0) {
    return modelValue.daysOfWeek;
  }
  return ["MONDAY"];
};

/** Result for frequency label i18n: key and optional params (e.g. { n: 2 }). */
export const getFrequencyLabelKey = (frequency: ScheduleFrequencyPayload): { key: string; params?: { n: number } } => {
  const type = frequency.type ?? "DAILY";
  const interval = Math.max(1, Math.min(6, frequency.interval ?? 1));
  const base = "CENTRALISED_REPORTING.SCHEDULES_TABLE.";
  if (type === "DAILY") {
    if (interval === 1) return { key: `${base}EVERY_DAY` };
    return { key: `${base}EVERY_N_DAYS`, params: { n: interval } };
  }
  if (type === "WEEKLY") {
    if (interval === 1) return { key: `${base}EVERY_WEEK` };
    if (interval === 2) return { key: `${base}EVERY_ALTERNATIVE_WEEK` };
    return { key: `${base}EVERY_N_WEEKS`, params: { n: interval } };
  }
  if (type === "MONTHLY") {
    if (interval === 1) return { key: `${base}EVERY_MONTH` };
    return { key: `${base}EVERY_N_MONTHS`, params: { n: interval } };
  }
  return { key: `${base}EVERY_DAY` };
};

/**
 * Formats schedule expiryTime (ISO string) to YYYY-MM-DD for display.
 */
export const formatExpiryDate = (expiryTime: string): string => {
  if (!expiryTime) return "";
  const dt = DateTime.fromISO(expiryTime);
  return dt.isValid ? (dt.toISODate() ?? expiryTime) : expiryTime;
};

/** Form model shape used by CreateSchedule when initializing from a saved schedule (e.g. edit). */
export interface ScheduleFormValue {
  scheduleName?: string;
  frequency?: ScheduleFrequency;
  everyNumber?: number;
  everyUnit?: EveryUnit;
  daysOfWeek?: string[];
  dayOfMonth?: number;
  time?: string;
  expiryDate?: string;
  emailRecipients?: string;
}

/**
 * Maps a TemplateSchedulePayload (from store/API) to the form model for CreateSchedule (e.g. for edit).
 */
export const schedulePayloadToFormValue = (schedule: TemplateSchedulePayload): ScheduleFormValue => {
  const f = schedule.frequency;
  return {
    frequency: f.type as ScheduleFrequency,
    everyNumber: f.interval ?? 1,
    everyUnit: getEveryUnitForFrequency(f.type as ScheduleFrequency),
    daysOfWeek: f.daysOfWeek ?? [],
    dayOfMonth: f.daysOfMonth?.[0] ?? 1,
    time: f.time ?? "09:00",
    expiryDate: formatExpiryDate(schedule.expiryTime),
    emailRecipients: (schedule.recipients ?? []).join(", ")
  };
};
